import { useCallback, useState } from "react";

// i18n léger du Workspace Délibéré (fr / en / pt — exigence OHADA).
// Périmètre volontairement limité au Coffre-fort du Délibéré.
export type Lang = "fr" | "en" | "pt";

const KEY = "gsf_delib_lang";

const STR: Record<string, Record<Lang, string>> = {
  title: { fr: "Coffre-fort du Délibéré", en: "Deliberation Vault", pt: "Cofre do Deliberativo" },
  back: { fr: "← Dossier", en: "← Case", pt: "← Processo" },
  loading: { fr: "Chargement…", en: "Loading…", pt: "A carregar…" },
  access_denied: {
    fr: "Accès réservé à l'arbitre et au greffier de ce dossier (secret du délibéré).",
    en: "Restricted to the arbitrator and registrar of this case (secrecy of deliberations).",
    pt: "Acesso reservado ao árbitro e ao secretário deste processo (segredo da deliberação).",
  },
  consent_title: { fr: "Consentement de délibération requis", en: "Deliberation consent required", pt: "Consentimento de deliberação necessário" },
  consent_text: {
    fr: "L'IA assiste, l'arbitre reste seul juge ; toute suggestion doit être validée/modifiée/rejetée ; la traçabilité est opposable ; le secret du délibéré est une obligation légale.",
    en: "AI assists, the arbitrator remains sole judge; every suggestion must be validated/modified/rejected; traceability is enforceable; secrecy of deliberations is a legal obligation.",
    pt: "A IA assiste, o árbitro permanece o único juiz; cada sugestão deve ser validada/modificada/rejeitada; a rastreabilidade é oponível; o segredo da deliberação é uma obrigação legal.",
  },
  accept: { fr: "J'accepte", en: "I accept", pt: "Aceito" },
  lock_section: { fr: "Verrouillage du délibéré", en: "Deliberation lock", pt: "Bloqueio da deliberação" },
  open_session: { fr: "Ouvrir une session", en: "Open a session", pt: "Abrir uma sessão" },
  close_instruction: { fr: "Clôturer l'instruction", en: "Close the instruction", pt: "Encerrar a instrução" },
  escalade: { fr: "Escalade OHADA requise", en: "OHADA escalation required", pt: "Escalonamento OHADA necessário" },
  concordance_section: { fr: "Concordance (validation obligatoire avant sentence)", en: "Concordance (mandatory validation before award)", pt: "Concordância (validação obrigatória antes da sentença)" },
  ready_gen: { fr: "prêt pour génération", en: "ready for generation", pt: "pronto para geração" },
  treated: { fr: "traités", en: "treated", pt: "tratados" },
  discarded: { fr: "écartés", en: "discarded", pt: "rejeitados" },
  new_chef: { fr: "Nouveau chef de demande…", en: "New head of claim…", pt: "Novo pedido…" },
  add: { fr: "Ajouter", en: "Add", pt: "Adicionar" },
  validate: { fr: "Valider", en: "Validate", pt: "Validar" },
  discard: { fr: "Écarter", en: "Discard", pt: "Rejeitar" },
  motif_prompt: { fr: "Motif d'écart :", en: "Reason for discarding:", pt: "Motivo da rejeição:" },
  notes_section: { fr: "Notes délibératoires (secret du délibéré)", en: "Deliberative notes (secrecy)", pt: "Notas deliberativas (segredo)" },
  new_note: { fr: "Nouvelle note délibératoire…", en: "New deliberative note…", pt: "Nova nota deliberativa…" },
  save_note: { fr: "Enregistrer la note", en: "Save note", pt: "Guardar nota" },
  votes_section: { fr: "Votes & opinions (tribunal collégial)", en: "Votes & opinions (collegial tribunal)", pt: "Votos & opiniões (tribunal colegial)" },
  vote_majority: { fr: "Majorité", en: "Majority", pt: "Maioria" },
  vote_concurring: { fr: "Concordante", en: "Concurring", pt: "Concordante" },
  vote_dissenting: { fr: "Dissidente", en: "Dissenting", pt: "Dissidente" },
  opinion_opt: { fr: "Opinion (facultatif)…", en: "Opinion (optional)…", pt: "Opinião (opcional)…" },
  vote: { fr: "Voter", en: "Vote", pt: "Votar" },
  versions_section: { fr: "Versions de sentence", en: "Award versions", pt: "Versões da sentença" },
  export: { fr: "Exporter", en: "Export", pt: "Exportar" },
  final: { fr: "FINALE", en: "FINAL", pt: "FINAL" },
  dispositif_ph: { fr: "Dispositif de la sentence…", en: "Operative part of the award…", pt: "Dispositivo da sentença…" },
  new_version: { fr: "Nouvelle version", en: "New version", pt: "Nova versão" },
  template_section: { fr: "Modèle de sentence (Art. 20 AUA)", en: "Award template (Art. 20 AUA)", pt: "Modelo de sentença (Art. 20 AUA)" },
  br_arm: { fr: "Armer", en: "Arm", pt: "Armar" },
  br_confirm: { fr: "Confirmer", en: "Confirm", pt: "Confirmar" },
  br_cancel: { fr: "Annuler", en: "Cancel", pt: "Cancelar" },
  br_resp: { fr: "J'accepte la responsabilité de cette signature exécutoire.", en: "I accept responsibility for this enforceable signature.", pt: "Aceito a responsabilidade desta assinatura executória." },
  br_mfa: { fr: "MFA vérifiée (OTP).", en: "MFA verified (OTP).", pt: "MFA verificado (OTP)." },
  signed: { fr: "signée", en: "signed", pt: "assinada" },
};

export function useDeliberationI18n() {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const s = localStorage.getItem(KEY);
      return s === "en" || s === "pt" ? s : "fr";
    } catch {
      return "fr";
    }
  });
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
  }, []);
  const t = useCallback((k: string) => STR[k]?.[lang] ?? STR[k]?.fr ?? k, [lang]);
  return { lang, setLang, t };
}
